import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCopy } from '../../copy/CopyProvider';
import { ApiError } from '../../lib/apiClient';
import {
  submitRequest, confirmRsvp, joinWaitlist, redeemInvitation, rememberAdmission,
  type PublicAdmission, type SubmissionResult,
} from '../../lib/admissions';
import type { EventDetail } from '../../lib/catalog';

type Kind = 'request' | 'rsvp' | 'waitlist' | 'invitation';

/**
 * One form for every admission kind.
 *
 * ── THE SAME FIELDS, BECAUSE THEY ASK FOR THE SAME THING ─────────────────────────────
 *
 * §50/§51/§52. A request, an RSVP and a waitlist entry all need a name, an email and a party
 * size. Building three forms would mean three places to keep the validation, the idempotency
 * key and the party bounds consistent — and §52 explicitly says not to make somebody retype
 * their details when a full RSVP becomes a waitlist join, which one shared form gets for free.
 *
 * What differs is the WORDING and the OUTCOME, and those are the two things this switches on.
 *
 * ── NO FIELD WITHOUT A REASON ────────────────────────────────────────────────────────
 *
 * §67/§68: no date of birth, no accommodation free-text, no address. Phone is offered only
 * because an operator running a door sometimes needs to reach somebody, and it is optional.
 */
export function AdmissionForm({
  event, admission, kind, onCancel,
}: {
  event: EventDetail;
  admission: PublicAdmission;
  kind: Kind;
  onCancel: () => void;
}) {
  const t = useCopy();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [partySize, setPartySize] = useState(admission.partySizeMin);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SubmissionResult | null>(null);
  // Generated once per mounted form, so a double-click is the same submission rather than a
  // second one. §12: the server returns the first result for a repeated key.
  const idempotencyKey = useMemo(
    () => `${kind}-${event.id}-${Math.random().toString(36).slice(2)}-${Date.now()}`,
    [kind, event.id],
  );

  const copy = FORM_COPY[kind];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      eventId: event.id, firstName, lastName, email, partySize, idempotencyKey,
    };
    try {
      const result = kind === 'rsvp' ? await confirmRsvp(body)
        : kind === 'request' ? await submitRequest(body)
          : kind === 'waitlist' ? await joinWaitlist(body)
            : await redeemInvitation({ ...body, token: token.trim() });

      if (result.accessToken) {
        rememberAdmission(event.id, {
          admissionId: result.admissionId ?? result.orderId ?? '',
          token: result.accessToken,
          kind: result.kind,
        });
      }
      setDone(result);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) return <Outcome kind={kind} result={done} t={t} />;

  const rangeHint = admission.partySizeMin === admission.partySizeMax
    ? null
    : `${admission.partySizeMin}–${admission.partySizeMax}`;

  return (
    <form className="adm-form-public" onSubmit={submit}>
      <h2 className="tickets__title">{t(copy.key, copy.fallback)}</h2>
      {/* §50: stated plainly, before anybody fills anything in. */}
      {copy.caveat && (
        <p className="adm-form-public__caveat">{t(copy.caveatKey, copy.caveatFallback)}</p>
      )}

      {error && <p className="alert alert--error" role="alert">{error}</p>}

      {kind === 'invitation' && (
        <label className="field">
          <span className="field__label">Invitation code</span>
          <input
            className="field__input" value={token} required autoComplete="off"
            spellCheck={false} onChange={(ev) => setToken(ev.target.value)}
          />
          <span className="field__help">From the link or code you were sent.</span>
        </label>
      )}

      <div className="adm-form-public__names">
        <label className="field">
          <span className="field__label">First name</span>
          <input className="field__input" value={firstName} required maxLength={100}
            autoComplete="given-name" onChange={(ev) => setFirstName(ev.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Last name</span>
          <input className="field__input" value={lastName} required maxLength={100}
            autoComplete="family-name" onChange={(ev) => setLastName(ev.target.value)} />
        </label>
      </div>

      <label className="field">
        <span className="field__label">Email</span>
        <input className="field__input" type="email" value={email} required maxLength={254}
          autoComplete="email" onChange={(ev) => setEmail(ev.target.value)} />
        <span className="field__help">We send your confirmation and any updates here.</span>
      </label>

      <label className="field">
        <span className="field__label">{t('admissions.partySize.label', 'How many people?')}</span>
        <input
          className="field__input"
          type="number"
          value={partySize}
          min={admission.partySizeMin}
          max={admission.partySizeMax}
          required
          onChange={(ev) => setPartySize(Number(ev.target.value))}
        />
        {/* The bounds are a courtesy here. The server enforces them (§13). */}
        {rangeHint && <span className="field__help">{rangeHint} people.</span>}
      </label>

      <div className="adm-form-public__actions">
        <button type="submit" className="button button--primary button--block" disabled={busy}>
          {busy ? 'Sending…' : t(copy.key, copy.fallback)}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Back
        </button>
      </div>
    </form>
  );
}

/** What happened. Each kind says something materially different, and none of them lies. */
function Outcome({
  kind, result, t,
}: {
  kind: Kind;
  result: SubmissionResult;
  t: ReturnType<typeof useCopy>;
}) {
  // A confirmed admission has tickets. §17: the same ticket page a buyer gets.
  const confirmed = kind === 'rsvp' || kind === 'invitation';

  if (confirmed) {
    return (
      <div className="adm-outcome adm-outcome--confirmed" role="status">
        <h2 className="tickets__title">
          {t('admissions.rsvp.confirmed.heading', "You're going")}
        </h2>
        <p>
          {result.partySize === 1
            ? 'Your ticket is on its way by email.'
            : `${result.partySize} tickets are on their way by email.`}
        </p>
        {result.orderId && result.accessToken && (
          <Link
            className="button button--primary button--block"
            to={`/orders/${result.orderId}/tickets?token=${encodeURIComponent(result.accessToken)}`}
          >
            View your tickets
          </Link>
        )}
      </div>
    );
  }

  if (kind === 'waitlist') {
    return (
      <div className="adm-outcome" role="status">
        <h2 className="tickets__title">
          {t('admissions.waitlist.joined.heading', "You're on the waitlist")}
        </h2>
        {/* §23: no position. Saying "you are #17" would be a promise this cannot keep. */}
        <p>{t('admissions.waitlist.joined.body', "We'll email you if a place opens up.")}</p>
        {result.admissionId && result.accessToken && (
          <Link
            className="button button--ghost button--block"
            to={`/admissions/${result.admissionId}/status?token=${encodeURIComponent(result.accessToken)}`}
          >
            Check your status
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="adm-outcome" role="status">
      <h2 className="tickets__title">
        {t('admissions.request.received.heading', 'Your request has been received')}
      </h2>
      {/* §18: NOT "you're going". No QR, no ticket link, and the wording says why. */}
      <p>
        {t('admissions.request.received.body',
          "We'll review it and email you either way. Submitting a request does not guarantee "
          + 'admission.')}
      </p>
      {result.admissionId && result.accessToken && (
        <Link
          className="button button--ghost button--block"
          to={`/admissions/${result.admissionId}/status?token=${encodeURIComponent(result.accessToken)}`}
        >
          Check your request
        </Link>
      )}
    </div>
  );
}

/**
 * The wording for each kind.
 *
 * ── ONE KEY, ONE DEFAULT ─────────────────────────────────────────────────────────────
 *
 * The heading and the submit button share a Copy key AND its fallback, so overriding
 * `admissions.rsvp.cta` once changes both and they cannot disagree. An earlier version
 * passed different fallbacks to the same key — the opening button said "RSVP" and the submit
 * button said "Confirm" until somebody set an override, at which point they silently became
 * identical. One key meaning two things is a key that will be edited to fix one place and
 * break the other.
 */
const FORM_COPY: Record<Kind, {
  key: string; fallback: string; caveat?: string; caveatKey: string; caveatFallback: string;
}> = {
  request: {
    key: 'admissions.request.cta', fallback: 'Request tickets',
    caveat: 'Submitting a request does not guarantee admission.',
    caveatKey: 'admissions.request.received.body',
    caveatFallback: "We'll review it and email you either way. Submitting a request does not "
      + 'guarantee admission.',
  },
  rsvp: {
    key: 'admissions.rsvp.cta', fallback: 'RSVP',
    caveatKey: 'admissions.rsvp.cta', caveatFallback: 'RSVP',
  },
  waitlist: {
    key: 'admissions.waitlist.cta', fallback: 'Join the waitlist',
    caveat: "We'll email you if a place opens up.",
    caveatKey: 'admissions.waitlist.joined.body',
    caveatFallback: "We'll email you if a place opens up.",
  },
  invitation: {
    key: 'admissions.invite.cta', fallback: 'Redeem an invitation',
    caveatKey: 'admissions.invite.required',
    caveatFallback: 'This event is by invitation only.',
  },
};

/**
 * A refusal, as a sentence.
 *
 * The engine sends a `message` for everything an attendee has to act on, so this prefers it
 * over any mapping kept here — a second copy of those sentences in the browser would drift
 * from the ones the server actually enforces.
 */
function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { message?: string; error?: string } | null;
    if (payload?.message) return payload.message;
    if (err.status === 429) return 'Too many attempts just now. Please try again shortly.';
    if (payload?.error === 'request_already_pending') {
      return 'You already have a request for this event awaiting review.';
    }
    if (payload?.error === 'already_on_waitlist') {
      return 'You are already on the waitlist for this event.';
    }
  }
  return 'Something went wrong. Please try again.';
}
